import { useFormik } from "formik";
import { Link, useNavigate } from "react-router-dom";
import AuthLayout from "components/Layout/AuthLayout";
import { navLinks } from "constants/paths";
import GoogleLogin from "react-google-login";
import { useGoogleMutation, useLoginMutation } from "redux/services/auth";
import FormInput from "components/FormInput";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { onLogin } from "redux/authSlice";
import { Button, Stack } from "react-bootstrap";
import GoogleIcon from "assets/images/google.svg";
import { ReactComponent as MobileIcon } from "assets/images/mobile.svg";
import { LoginSchema } from "constants/ValidationSchema";
import {
    deleteAllTokens,
    deleteInviteToken,
    getAllInviteTokens,
} from "helper/inviteTokenHelper";
import "./Login.scss";
import { useTranslation } from "react-i18next";

function registerSW() {
    if ("serviceWorker" in navigator) {
        window.addEventListener("load", () => {
            navigator.serviceWorker
                .register("./serviceWorker.js")
                .then((reg) => {
                    console.log("Worker Registered");
                })
                .catch((err) => {
                    console.log("Error in service worker registration.");
                });
        });
    }
}

const Login = () => {
    const { t } = useTranslation();
    const [login, { isLoading }] = useLoginMutation();
    const [google] = useGoogleMutation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    registerSW();
    const formik = useFormik({
        initialValues: {
            email: "",
            password: "",
        },
        validationSchema: LoginSchema,
        onSubmit: async (values) => {
            const inviteToken = localStorage.getItem("inviteToken");
            const res: any = await login({ body: values, inviteToken });
            if (res.data) {
                onSubmit(res.data);
            } else toast.error(res.error.data.message);
        },
    });

    const onSubmit = (values: any) => {
        localStorage.setItem("c360labs/user-details", JSON.stringify(values));
        dispatch(onLogin());
        if (values.inviteMessage) {
            toast[values.inviteSuccess ? "success" : "error"](
                values.inviteMessage
            );
            deleteInviteToken();
        } else {
            const messages = getAllInviteTokens();
            messages.forEach((token) => {
                toast.success(token.message);
            });
            deleteAllTokens();
        }
        navigate("/onboarding");
        toast.success(t("login_success"));
    };

    const responseGoogle = async (response: any) => {
        if (response) {
            const values = {
                email: response.profileObj.email,
                name: response.profileObj.name,
                imageUrl: response.profileObj.imageUrl,
            };
            const inviteToken = localStorage.getItem("inviteToken");
            const res: any = await google({ body: values, inviteToken });

            if (res.data) {
                onSubmit(res.data);
            } else
                toast.error(
                    res.error.data.message || t("something_went_wrong")
                );
        }
    };

    const handleFailure = (result: any) => {
        console.log(JSON.stringify(result));
    };

    const { errors, touched } = formik;
    return (
        <AuthLayout>
            <div className="lgBox login">
                <h2>{t("login")}</h2>
                <p className="text-center tag-line invisible">
                    {t("login_branding")}
                </p>
                <form
                    onSubmit={formik.handleSubmit}
                    noValidate
                    className="mb-2"
                >
                    <FormInput
                        label="Email"
                        name="email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        touched={touched.email}
                        error={errors.email}
                    />
                    <FormInput
                        label="Password"
                        type="password"
                        name="password"
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        touched={touched.password}
                        error={errors.password}
                    />
                    <div className="forgot text-end mb-3">
                        <Link to={navLinks.forgotPassowrd}>
                            {t("forgot_password")}
                        </Link>
                    </div>
                    <button
                        type="submit"
                        className="btn btn-primary w-100"
                        disabled={isLoading}
                    >
                        {t("login")}
                    </button>
                </form>
                <p className="text-center mb-2">{t("or")}</p>
                <Stack direction="horizontal" gap={3} className="mb-3">
                    <GoogleLogin
                        clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ""}
                        render={(renderProps: any) => (
                            <Button
                                variant="outline-primary"
                                className="d-flex align-items-center justify-content-center w-100"
                                onClick={renderProps.onClick}
                                disabled={renderProps.disabled}
                            >
                                <img
                                    alt={t("google")}
                                    src={GoogleIcon}
                                    width="20"
                                />
                                <span className="ms-1">{t("google")}</span>
                            </Button>
                        )}
                        onSuccess={responseGoogle}
                        onFailure={handleFailure}
                        cookiePolicy={"single_host_origin"}
                    />
                    <Link
                        to={navLinks.mobile}
                        className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center"
                    >
                        <MobileIcon width={18} />
                        <span className="ms-1">{t("mobile")}</span>
                    </Link>
                </Stack>
                <div className="form-group donthave">
                    <p className="text-center mb-0">
                        {t("dont_have_account")}{" "}
                        <Link to={navLinks.register}>{t("register_now")}</Link>
                    </p>
                </div>
            </div>
        </AuthLayout>
    );
};

export default Login;
